import React, { useState } from 'react';
import { showToast } from '@/components/ui/Toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import { 
  Home, 
  CheckCircle2, 
  ArrowRight, 
  ExternalLink, 
  Scale, 
  Loader2 
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { AppAction, UserProfile } from '@/types';

// IMPORTANT: This must match the LEGAL_VERSION constant in App.tsx gates exactly
export const LEGAL_VERSION = "2026-02-12";

interface OnboardingLegalProps {
  user: UserProfile | null; // Strictly typed to our consolidated UserProfile
  dispatch: React.Dispatch<AppAction>;
}

/**
 * ⚖️ OnboardingLegal Gate
 * A mandatory compliance portal that captures user consent
 * before allowing access to the Household OS data streams.
 */
export const OnboardingLegal: React.FC<OnboardingLegalProps> = ({ user, dispatch }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Local State for Compliance Checks
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [isOverAge, setIsOverAge] = useState(false); 
  const [loading, setLoading] = useState(false);

  const canSubmit = agreedToTerms && agreedToPrivacy && isOverAge;

  /**
   * 🛰️ Synchronize Legal Handshake
   * Persists consent metadata to Firestore and updates local state.
   */
  const handleAcceptance = async () => {
    if (!canSubmit || loading || !user?.id) return;
    
    setLoading(true);
    try {
      const userRef = doc(db, "users", user.id);
      
      const legalData = {
        acceptedLegalVersion: LEGAL_VERSION,
        legalConsentDate: serverTimestamp(),
        isAgeVerified: true, 
        consentMetadata: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          jurisdiction: Intl.DateTimeFormat().resolvedOptions().timeZone,
          confirmedAge: "16+"
        }
      };

      await updateDoc(userRef, legalData);

      // Sync local state via Dispatch
      dispatch({
        type: 'UPDATE_USER', // Use UPDATE_USER for partial updates
        payload: {
          acceptedLegalVersion: LEGAL_VERSION, // Explicitly update this field
          legalConsentDate: new Date().toISOString(), // Use JS Date for local state sync
          isAgeVerified: true,
          // ... (any other fields from legalData you want to reflect immediately)
          // No need to spread ...user if you're using UPDATE_USER for specific fields
        } as Partial<UserProfile> // Cast as Partial<UserProfile>
      });

      // Navigate to next gate (Disclaimer)
      navigate('/disclaimer', { replace: true });
      
    } catch (error) {
      console.error("Critical: Legal Protocol Handshake Failed:", error);
      showToast(t('legal.error_save', 'Failed to synchronize legal consent. Check connection.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex items-center justify-center p-6 transition-colors duration-500 overflow-hidden relative">
      
      {/* 🎨 Background Decoration */}
      <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-500/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-xl w-full bg-white dark:bg-slate-900 rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-500 relative z-10">
        
        {/* --- HEADER VISUAL --- */}
        <div className="bg-brand p-8 text-center text-white relative overflow-hidden">
          <div className="inline-flex p-3 rounded-2xl bg-white/20 backdrop-blur-md mb-4 relative z-10">
            <Home className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-black tracking-tight relative z-10">

            {t('legal.title', 'Terms of Protocol')}
          </h1>
          <p className="text-indigo-100 font-medium text-sm mt-2 leading-tight relative z-10">
            {t('legal.subtitle', 'Initialize your residency in the EverydayOS ecosystem.')}'
          </p>
          <Scale className="absolute -bottom-6 -right-6 w-32 h-32 text-white/10 rotate-12" />
        </div>

        <div className="p-10">
          <div className="space-y-4">
            
            {/* Age Verification Checkbox */}
            <label className={`flex items-start gap-4 p-5 rounded-2xl border-2 transition-all cursor-pointer ${
              isOverAge 
                ? 'border-emerald-500 bg-emerald-500/5 dark:bg-emerald-500/10' 
                : 'border-slate-100 dark:border-slate-800 hover:border-slate-200 dark:hover:border-slate-700'
            }`}>
              <div className="mt-1">
                <input 
                  type="checkbox" 
                  checked={isOverAge}
                  onChange={(e) => setIsOverAge(e.target.checked)}
                  className="w-5 h-5 rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950 text-emerald-500 focus:ring-emerald-500 cursor-pointer"
                />
              </div>
              <div className="flex-1">
                <p className="font-bold text-slate-900 dark:text-gray-200 leading-tight">

                  {t('legal.age_check', 'I verify that I meet the age requirements.')}
                </p>
                <p className="text-2xs text-slate-500 dark:text-gray-400 mt-1 uppercase font-black tracking-widest">

                  {t('legal.age_label', 'Minimum Age Protocol: 16+')}
                </p>
              </div>
            </label>

            {/* TOS Checkbox */}
            <label className={`flex items-start gap-4 p-5 rounded-2xl border-2 transition-all cursor-pointer ${
              agreedToTerms 
                ? 'border-brand bg-brand/5 dark:bg-brand/10' 
                : 'border-slate-100 dark:border-slate-800 hover:border-slate-200 dark:hover:border-slate-700'
            }`}>
              <div className="mt-1">
                <input 
                  type="checkbox" 
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="w-5 h-5 rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950 text-brand focus:ring-brand cursor-pointer"
                />
              </div>
              <div className="flex-1">
                <p className="font-bold text-slate-900 dark:text-gray-200 leading-tight">
                  {t('legal.terms_check', 'I have audited the Terms of Service.')}
                </p>
                <a 
                  href="/terms" 
                  target="_blank" 
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-2xs font-black uppercase tracking-widest text-brand dark:text-indigo-400 mt-2 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('legal.review_terms', 'Review Terms')}' <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </label>

            {/* Privacy Checkbox */}
            <label className={`flex items-start gap-4 p-5 rounded-2xl border-2 transition-all cursor-pointer ${
              agreedToPrivacy
                ? 'border-brand bg-brand/5 dark:bg-brand/10'
                : 'border-slate-100 dark:border-slate-800 hover:border-slate-200 dark:hover:border-slate-700'
            }`}>
              <div className="mt-1">
                <input
                  type="checkbox"
                  checked={agreedToPrivacy}
                  onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                  className="w-5 h-5 rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950 text-brand focus:ring-brand cursor-pointer"
                />
              </div>
              <div className="flex-1">
                <p className="font-bold text-slate-900 dark:text-gray-200 leading-tight">
                  {t('legal.privacy_check', 'I accept the Privacy & Data Protocol.')}
                </p>
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-2xs font-black uppercase tracking-widest text-brand dark:text-indigo-400 mt-2 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('legal.review_policy', 'Review Privacy')} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </label>
          </div>

          <div className="mt-10">
            <Button
              onClick={handleAcceptance}
              disabled={!canSubmit || loading}
              size="lg"
              className="w-full rounded-2xl h-16 font-black text-lg bg-brand text-white shadow-xl shadow-brand/20 group transition-all"
            >
              {loading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  {t('legal.cta', 'Synchronize & Continue')} 
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
            
            <p className="text-center text-2xs font-black uppercase tracking-widest text-slate-400 dark:text-gray-500 mt-6">
              {t('legal.version_info', { version: LEGAL_VERSION })}
            </p>
          </div>
        </div>

        {/* --- COMPLIANCE FOOTER --- */}
        <div className="bg-slate-50 dark:bg-slate-800 p-4 flex items-center justify-center gap-2 border-t border-slate-100 dark:border-slate-700">
          <CheckCircle2 className={`w-4 h-4 transition-colors ${canSubmit ? 'text-emerald-500' : 'text-slate-300 dark:text-slate-700'}`} />
          <span className="text-2xs font-bold text-slate-400 dark:text-gray-500 tracking-tight">
            {t('legal.compliance_badge', 'SECURE DATA ENCRYPTION ACTIVE')}
          </span>
        </div>
      </div>
    </div>
  );
};